import { useState } from "react";

import trackingService from "app/pages/sales-intelligence/sub-modules/advanced-search/services/tracking/advancedSearchTrackingService";
import AddProfile from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/components/AddProfile/AddProfile";
import EmployeeProfileContainer from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/components/EmployeeProfile/EmployeeProfileContainer";
import EmployeeProfilesEmptyState from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/components/EmployeeProfilesEmptyState/EmployeeProfilesEmptyState";
import RemoveProfileModalContainer from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/components/RemoveProfileModal/RemoveProfileModalContainer";
import { UNSAVED_PROFILE_ID } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/constants/common";
import { isSharedEmployeeProfile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/accessLevelFilters";
import {
    createEmptyProfile,
    generateDummyNameForNextProfile,
    isProfileNotEmpty,
} from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/common";
import type { Profile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/common";
import type { ProfileModalState } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/state";

import { UpdateEventName } from "./types";

interface IProps {
    value: readonly Profile[];
    seniority: readonly string[];
    departments: readonly string[];
    isAddProfileButtonDisabled: boolean;
    canManageShared: boolean;
    onUpdateLocalState: (profiles: readonly Profile[], eventName: UpdateEventName) => void;
    onUpdateFilterAndLocalState: (profiles: readonly Profile[], eventName: UpdateEventName) => void;
    onRemoveSuccess?: () => void;
    onSaveSuccess?: () => void;
}

const EmployeeProfilesFilter = ({
    value,
    seniority,
    departments,
    isAddProfileButtonDisabled,
    canManageShared,
    onUpdateLocalState,
    onUpdateFilterAndLocalState,
}: IProps) => {
    const [removeModalState, setRemoveModalState] = useState<ProfileModalState>({
        profile: null,
        isOpened: false,
    });

    function handleProfileRemove(id: Profile["id"]) {
        setRemoveModalState({ profile: null, isOpened: false });

        const profile = value.find((p) => p.id === id);

        if (!profile) {
            return;
        }

        trackingService.contactsFilters.trackProfileDeleted(profile.name);

        const profiles = value.filter((p) => p.id !== id);

        onUpdateFilterAndLocalState(profiles, UpdateEventName.Remove);
    }

    function handleProfileRename(profile: Profile) {
        const oldProfile = value.find((p) => p.id === profile.id);

        if (!oldProfile) {
            return;
        }

        trackingService.contactsFilters.trackProfileRenamed(oldProfile.name, profile.name);

        const profiles = value.map((p) => {
            if (profile.id === p.id) {
                return profile;
            }

            return p;
        });

        onUpdateLocalState(profiles, UpdateEventName.Rename);
    }

    function handleProfileSave(profile: Profile) {
        const profiles = value.map((p) => {
            if (p.id === UNSAVED_PROFILE_ID) {
                return profile;
            }

            return p;
        });

        if (profile.isEnabled) {
            return onUpdateFilterAndLocalState(profiles, UpdateEventName.Saved);
        }

        onUpdateLocalState(profiles, UpdateEventName.Saved);
    }

    function handleProfileUpdate(profile: Profile) {
        const profiles = value.map((p) => {
            if (profile.id === p.id) {
                return profile;
            }

            return p;
        });

        if (profile.isEnabled) {
            return onUpdateFilterAndLocalState(profiles, UpdateEventName.Update);
        }

        onUpdateLocalState(profiles, UpdateEventName.Update);
    }

    function handleItemEnabledToggle(profile: Profile) {
        trackProfileEnabledToggle(profile);

        const profiles = value.map((p) => {
            if (profile.id === p.id) {
                return {
                    ...profile,
                    isEnabled: !profile.isEnabled,
                };
            }

            return p;
        });

        if (isProfileNotEmpty(profile)) {
            return onUpdateFilterAndLocalState(profiles, UpdateEventName.EnabledToggle);
        }

        onUpdateLocalState(profiles, UpdateEventName.EnabledToggle);
    }

    function handleItemExpandToggle(id: Profile["id"]) {
        trackProfileExpandToggle(id);

        const profiles = value.map((p) => {
            if (p.id === id) {
                return {
                    ...p,
                    isExpanded: !p.isExpanded,
                };
            }

            return {
                ...p,
                isExpanded: false,
            };
        });

        onUpdateLocalState(profiles, UpdateEventName.ExpandToggle);
    }

    function addAnotherProfile() {
        const profiles = [
            ...value.map((p) => ({ ...p, isExpanded: false })),
            createEmptyProfile(generateDummyNameForNextProfile(value)),
        ];

        onUpdateLocalState(profiles, UpdateEventName.AddProfile);
    }

    function handleAddProfileClicked() {
        trackingService.contactsFilters.trackAddProfileClicked(value.length);
        addAnotherProfile();
    }

    function handleEmptyStateDismissed() {
        trackingService.contactsFilters.trackProfilesEmptyStateDismissed();
        addAnotherProfile();
    }

    function trackProfileExpandToggle(id: Profile["id"]) {
        const profile = value.find((p) => p.id === id);

        if (!profile) {
            return;
        }

        if (profile.isExpanded) {
            trackingService.contactsFilters.trackProfileCollapsed(profile.name);
        } else {
            trackingService.contactsFilters.trackProfileExpanded(profile.name);
        }
    }

    function trackProfileEnabledToggle(profile: Profile) {
        if (profile.isEnabled) {
            trackingService.contactsFilters.trackProfileDisabled(profile.name);
        } else {
            trackingService.contactsFilters.trackProfileEnabled(profile.name);
        }
    }

    function renderProfilesOrEmpty() {
        if (value.length === 0) {
            return <EmployeeProfilesEmptyState onDismiss={handleEmptyStateDismissed} />;
        }

        return (
            <>
                <RemoveProfileModalContainer
                    {...removeModalState}
                    onRemoveSuccess={handleProfileRemove}
                    onCancel={() => setRemoveModalState({ profile: null, isOpened: false })}
                />
                {value.map((profile) => (
                    <EmployeeProfileContainer
                        profile={profile}
                        key={`profile-${profile.id}`}
                        seniority={seniority}
                        departments={departments}
                        isReadOnly={isSharedEmployeeProfile(profile) && !canManageShared}
                        onSaveSuccess={handleProfileSave}
                        onRenameSuccess={handleProfileRename}
                        onProfileChange={handleProfileUpdate}
                        onExpandToggle={handleItemExpandToggle}
                        onEnabledToggle={handleItemEnabledToggle}
                        onRemoveClick={() => setRemoveModalState({ profile, isOpened: true })}
                    />
                ))}
                <AddProfile
                    onAddClick={handleAddProfileClicked}
                    isDisabled={isAddProfileButtonDisabled}
                />
            </>
        );
    }

    return <div>{renderProfilesOrEmpty()}</div>;
};

export default EmployeeProfilesFilter;
